import { Button } from "reactstrap";
import { useState, useRef, useEffect } from "react";
import "react-circular-progressbar/dist/styles.css";
import axios from "axios";
import { motion } from "framer-motion";
import RotatePhonePopup from "../RotatePhonePopup";
import { useSnackbar } from "../context/SnackbarContext";
import { useLanguage } from "../context/Languagecontext";

import CompletePopup from "./completepopup";
import { convertToNativeNumerals } from "../../utils/numberUtils";
import DiceButton from "./DiceButton"; // ← shared dice button
import BadWordWarning from "./Badwordwarning";
import useBadWordDetection from "../../hooks/Usebadworddetection";


const Category10 = ({ hint, uploadedImage, originalData, username, category }) => {
    const { lanText, t } = useLanguage();
    const { showSnackbar } = useSnackbar();

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [userContent, setUserContent] = useState("");
    const { hasBadWord } = useBadWordDetection(userContent);
    const [isUploading, setIsUploading] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [cardAnimationComplete, setCardAnimationComplete] = useState(false);

    const textareaRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const topRef = useRef(null);

    const maxLength = 150;
    const typedChars = [...userContent].length;
    const remainingChars = maxLength - typedChars;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSmallScreen = window.innerWidth <= 380;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(ua);

    const isInAppBrowser = () =>
        /Instagram|FBAN|FBAV|Messenger|Telegram|Snapchat|Snap/i.test(
            navigator.userAgent || navigator.vendor || window.opera
        );

    // ─── Helpers ────────────────────────────────────────────────────────────────

    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const checkInternetConnection = async () => {
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (!navigator.onLine) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch {
            return false;
        }
    };

    // ─── Textarea ───────────────────────────────────────────────────────────────

    const handleContentChange = (e) => {
        if ([...e.target.value].length > maxLength) { triggerShake(); return; }
        setUserContent(e.target.value);
    };

    const handleFocus = (e) => {
        setKeyboardOpen(true);
        if (e?.preventDefault) e.preventDefault();
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const rect = topRef.current.getBoundingClientRect();
                    window.scrollTo({ top: rect.top + window.scrollY, behavior: "smooth" });
                }
            });
        }, 100);
    };

    const handleBlur = () => {
        setKeyboardOpen(false);
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    };

    // ─── Upload ─────────────────────────────────────────────────────────────────

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], fileName, { type: mime });
    };

    const upload = async () => {
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("id", originalData?.id || "");
            formData.append("category", category);
            formData.append("webCategory", category);
            formData.append("hint", hint || "");

            if (uploadedImage) {
                formData.append(
                    "uploadedImage",
                    uploadedImage.startsWith("data:image")
                        ? base64ToFile(uploadedImage, "hintImage.jpg")
                        : uploadedImage
                );
            }

            formData.append("userLocation", "allow");
            formData.append("deviceIp", localStorage.getItem("deviceIp") || "");
            formData.append("challengeContent", userContent || "");

            await axios.post("https://api.lolcards.link/api/newinbox/create", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const url = new URL(window.location.href);
            url.searchParams.set("success", "1");
            window.history.pushState({ success: true }, "", url.toString());
            setShowSuccessModal(true);
            setUserContent("");
        } catch (error) {
            console.error("Error uploading data:", error);
            showSnackbar(t?.common?.apiFail || "Request failed. Please try again after some time!", "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendClick = async () => {
        if (isUploading) return;
        setIsCheckingConnection(true);
        const isOnline = await checkInternetConnection();
        setIsCheckingConnection(false);

        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");
            return;
        }

        await upload();
        try {
            await axios.patch("https://api.lolcards.link/api/admin/monitoring", {
                categoryname: category,
                id: originalData?.id,
            });
        } catch (error) {
            console.error("Error updating monitoring:", error);
        }
    };

    // ─── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        const handlePopState = () => {
            if (showSuccessModal) {
                setShowSuccessModal(false);
                const url = new URL(window.location.href);
                if (url.searchParams.has("success")) {
                    url.searchParams.delete("success");
                    window.history.replaceState({}, "", url.pathname);
                }
            }
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [showSuccessModal]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("success") === "1") setShowSuccessModal(true);
    }, [showSuccessModal]);

    useEffect(() => {
        const timer = setTimeout(() => setCardAnimationComplete(true), 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const isDisabled = keyboardOpen || isUploading || isCheckingConnection;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [keyboardOpen, isUploading, isCheckingConnection]);

    // ─── Render ─────────────────────────────────────────────────────────────────

    return (
        <motion.div
            className="GTRegular"
            style={{ background: "#7D5EEE", height: "100dvh" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
        >
            <div className="h-100" style={{ position: "relative", zIndex: 2 }}>
                <div
                    className={`mx-auto justify-content-center h-100`}
                    style={{ width: "400px", maxWidth: "90vw", display: "flex", flexDirection: "column" }}
                >
                    <div className="text-center d-flex flex-column justify-content-start h-100">

                        {/* Title */}
                        <div
                            className="text-center py-4 pt-5"
                            style={{ maxWidth: "90vw", opacity: cardAnimationComplete ? 1 : 0, transition: "opacity 0.2s ease" }}
                        >
                            <p
                                className="lh-sm mx-auto px-3 m-0 text-white pt-3"
                                style={{ fontSize: "clamp(20px, 4.5vw, 22px)" }}
                            >
                                {t?.challenge?.title || "Give a challenge anonymously"}
                            </p>
                        </div>

                        {/* Card */}
                        <div
                            ref={topRef}
                            className="d-flex flex-column align-items-center justify-content-center mx-auto"
                            style={{
                                marginBottom: keyboardOpen && isAndroid ? "0px" : "0px",
                                paddingTop: isSmallScreen ? "10px" : "10px",
                                transition: "margin 0.3s ease",
                                width: "95%",
                            }}
                        >
                            <motion.div
                                className={`w-100 rounded-19 position-relative mt-2 glass-card ${isShaking ? "shake-animation" : ""}`}
                                style={{ minHeight: "240px" }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", duration: 1, delay: 0.2 }}
                            >
                                {/* Card header */}
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-19-top Bugs pt-2"
                                    style={{ height: "78px", background: "white", fontSize: "24px" }}
                                >
                                    {t?.challenge?.challenge || "Challenge"}
                                </div>

                                {/* Textarea + DiceButton */}
                                <div
                                    className="p-3 pb-1 d-flex align-items-center justify-content-center text-left"
                                    style={{
                                        height: "130px",
                                        fontSize: "19px",
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                        whiteSpace: "normal",
                                    }}
                                >
                                    <textarea
                                        ref={textareaRef}
                                        value={userContent}
                                        onChange={handleContentChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        placeholder={t?.challenge?.placeholder || "Type your challenge"}
                                        disabled={isUploading || isCheckingConnection}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") { e.preventDefault(); textareaRef.current?.blur(); }
                                        }}
                                        enterKeyHint="done"
                                        className="w-100 h-100 border-0 custom-placeholder-5 GTRegular text-white bg-transparent"
                                        style={{
                                            resize: "none",
                                            outline: "none",
                                            fontSize: "17px",
                                            fontWeight: "500",
                                            lineHeight: "22px",
                                            height: isSmallScreen ? "90px" : "100px",
                                            textAlign: "start",
                                            padding: "0px",
                                            opacity: 1,
                                        }}
                                    />

                                    {/* ↓ Replaces ~40 lines of button markup */}
                                    <DiceButton
                                        category={category}
                                        onContentFetched={(text) => setUserContent(text)}
                                        disabled={isUploading || isCheckingConnection}
                                    />
                                </div>
                            </motion.div>

                            {/* Char counter */}
                            <p
                                className={`text-end pe-3 w-100 text-white my-1 GTMedium ${remainingChars <= 0 ? "limit-reached" : ""}`}
                                style={{ opacity: cardAnimationComplete ? 1 : 0, transition: "opacity 0.2s ease" }}
                            >
                                {convertToNativeNumerals(typedChars, lanText)}/{convertToNativeNumerals(maxLength, lanText)}
                            </p>

                            <BadWordWarning
                                visible={hasBadWord}
                                message={
                                    t?.common?.badWordWarning ||
                                    "Avoid using offensive language"
                                }
                            />
                        </div>

                        {/* Send button */}
                        <div
                            className="w-100"
                            style={{ marginTop: "25px", opacity: cardAnimationComplete ? 1 : 0, transition: "opacity 0.2s ease" }}
                        >
                            <Button
                                className="rounded-pill border-0 mx-auto mb-2 text-white"
                                disabled={!userContent.trim()}
                                style={{
                                    padding: "12px 1px",
                                    height: "54px",
                                    width: "75%",
                                    cursor: !userContent.trim() ? "not-allowed" : "pointer",
                                    background: "black",
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                }}
                                onClick={handleSendClick}
                            >
                                {isUploading || isCheckingConnection ? (
                                    <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                                ) : (
                                    <span className="GTMedium" style={{ fontSize: "clamp(21px, 4.5vw, 27px)", whiteSpace: "nowrap" }}>
                                        {t?.common?.send || "Send"}
                                    </span>
                                )}
                            </Button>
                        </div>

                    </div>
                </div>
            </div>

            <RotatePhonePopup excludeWhen={[keyboardOpen]} />

            <CompletePopup
                showSuccessModal={showSuccessModal}
                toggle={() => setShowSuccessModal(false)}
                data={originalData}
                username={username}
                category={category}
            />
        </motion.div>
    );
};

export default Category10;